import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, getDoc, doc, onSnapshot, query, where, deleteDoc} from 'firebase/firestore';
// useNavigate removed - using window.location.href for reliable navigation
import zipcodes from 'zipcodes';
import { formatAge } from '../utils/dateHelpers';
import { CLOSED_STATUSES } from '../utils/applicationLifecycle';
import { auth, db } from '../firebaseConfig';

// ApplicationModal moved outside JobList to prevent re-creation on every render
const ApplicationModal = ({ isOpen, onClose, onSubmit, initialMessage }) => {
  const [localMessage, setLocalMessage] = useState('');
  const textareaRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Initialize message only when modal opens
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      setLocalMessage(initialMessage || '');
      hasInitializedRef.current = true;
      // Focus textarea after a short delay
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, initialMessage]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Application Message</h2>
                <p className="text-sm text-slate-600">Add a personal touch to your application</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-3">
              Your Message
            </label>
            <textarea
              ref={textareaRef}
              rows={4}
              value={localMessage}
              onChange={(e) => setLocalMessage(e.target.value)}
              placeholder="Tell the parent why you'd be great for this job..."
              className="w-full p-4 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200 resize-none"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all duration-200 border border-slate-200"
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Cancel</span>
              </span>
            </button>
            <button
              onClick={() => onSubmit(localMessage)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Submit Application</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatTimeToAMPM = (time24) => {
  if (!time24) return '';
  const [hourStr, minute] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${ampm}`;
};

function formatLocalDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
}

const JobTimesModal = ({ isOpen, onClose, job }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !job?.schedule) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Job Schedule</h2>
                <p className="text-sm text-slate-600">{job.title}</p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {job.schedule && Object.keys(job.schedule).length > 0 ? (
            <div className="space-y-4">
              {Object.keys(job.schedule)
                .sort((a, b) => new Date(a) - new Date(b))
                .map((date) => {
                  const ranges = Array.isArray(job.schedule[date])
                    ? job.schedule[date]
                    : [job.schedule[date]];

                  return (
                    <div key={date} className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="font-semibold text-slate-800">{formatLocalDate(date)}</h3>
                      </div>

                      <div className="space-y-2">
                        {ranges.map((range, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <span className="text-slate-700 font-medium">
                                {formatTimeToAMPM(range.start)} - {formatTimeToAMPM(range.end)}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                              Range {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-slate-500 font-medium">No days scheduled</p>
              <p className="text-sm text-slate-400">This job doesn't have a schedule set yet.</p>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-t border-slate-200/50 rounded-b-2xl flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-slate-500/25"
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Close</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxDistance, setMaxDistance] = useState(25); // Default maximum distance in kilometers
  const [isDistanceFilterEnabled, setIsDistanceFilterEnabled] = useState(false); // State to enable/disable distance filter
  const [userType, setUserType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [sortOption, setSortOption] = useState('datePosted'); // Sort by date posted by default
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [isTimesModalOpen, setIsTimesModalOpen] = useState(false);
  const [selectedJobForTimes, setSelectedJobForTimes] = useState(null);
  const [zipCode, setZipCode] = useState('');

  // Navigation uses window.location.href for reliability

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const applicationsQuery = query(
        collection(db, 'applications'),
        where('sitterId', '==', user.uid)
      );

      const applicationsSnapshot = await getDocs(applicationsQuery);
      const appliedJobIds = new Set();
      applicationsSnapshot.forEach((doc) => {
        // Only count LIVE applications — a closed/cancelled one shouldn't keep
        // showing "Applied" and block re-application on a reopened job.
        if (!CLOSED_STATUSES.includes(doc.data().status)) {
          appliedJobIds.add(doc.data().jobId);
        }
      });

      setAppliedJobs(appliedJobIds);
    };

    fetchAppliedJobs();
  }, []);

  const fetchCityAndState = async (latitude, longitude) => {
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDCCNLE719tfQRXV3rqPZBZDBOXR-UGy9E'}`);
    const data = await response.json();
    if (data.results.length > 0) {
      const addressComponents = data.results[0].address_components;
      const city = addressComponents.find(component => component.types.includes('locality'))?.long_name;
      const state = addressComponents.find(component => component.types.includes('administrative_area_level_1'))?.short_name;
      return { city, state };
    }
    return { city: null, state: null };
  };


  function getLatLngFromZip(zip) {
    const lookup = zipcodes.lookup(zip);
    return lookup ? { latitude: lookup.latitude, longitude: lookup.longitude } : null;
  }

  const haversineDistance = (coords1, coords2) => {
    const toRad = x => x * Math.PI / 180;
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = toRad(coords2.latitude - coords1.latitude);
    const dLon = toRad(coords2.longitude - coords1.longitude);
    const lat1 = toRad(coords1.latitude);
    const lat2 = toRad(coords2.latitude);
  
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in miles
  };


  useEffect(() => {
    const fetchUserType = async () => {
      const user = auth.currentUser;
      if (user) {
        const type = await getUserRole(user.uid);
        setUserType(type);
      }
      else {
        alert('User not authenticated');
        window.location.href = '/auth';
        return;
      }
    };
  
    fetchUserType();
  }, []);
  
  const getUserRole = async (userId) => {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().userType;
    }
    return null;
  };

  const applyForJob = async (jobId) => {
    const user = auth.currentUser;
    if (!user) {
      alert("You need to be logged in to apply for a job.");
      return;
    }

    const role = await getUserRole(user.uid);
    if (role !== 'sitter') {
      alert("Only sitters can apply for jobs.");
      return;
    }

    // Block reapplication only if the sitter has a LIVE application for this job.
    // A closed/cancelled/denied prior application must NOT lock them out — e.g.
    // if the family cancelled and reposted, the sitter should be able to reapply.
    const applicationsQuery = query(
      collection(db, 'applications'),
      where('jobId', '==', jobId),
      where('sitterId', '==', user.uid)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);

    const liveApplication = applicationsSnapshot.docs.find(
      (d) => !CLOSED_STATUSES.includes(d.data().status)
    );
    if (liveApplication) {
      alert("You already have an active application for this job.");
      return;
    }

    setSelectedJobId(jobId);
    setIsModalOpen(true);
  };


  const handleModalSubmit = async (message) => {
    try {
      if (!message || !message.trim()) {
        alert("Please write a short message to the parent before applying.");
        return;
      }
      const user = auth.currentUser;
      const role = await getUserRole(user.uid);
      if (role !== 'sitter') {
        alert("Only sitters can apply for jobs.");
        return;
      }

      // Fetch user data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        alert("User data not found.");
        return;
      }
      const userData = userDoc.data();

      // Fetch job data
      const jobDoc = await getDoc(doc(db, 'jobs', selectedJobId));
      if (!jobDoc.exists()) {
        alert("Job data not found.");
        return;
      }
      const jobData = jobDoc.data();
  
      const application = {
        jobId: selectedJobId,
        sitterId: user.uid,
        sitterName: userData.name,
        sitterEmail: userData.email,
        sitterProfilePictureURL: userData.profilePictureURL || null,
        sitterStripeAccountId: userData.stripeAccountId || null,
        message: message,
        appliedAt: new Date(),
        jobTitle: jobData.title,
        jobDescription: jobData.description,
        jobPayRange: jobData.payRange,
        jobStartDate: jobData.startDate,
        jobStartTime: jobData.jobStartTime,
        jobEndDate: jobData.endDate,
        jobAddress: jobData.address,
        jobCity: jobData.city,
        jobState: jobData.state,
        jobZipCode: jobData.zipCode,
        jobLocation: jobData.location,
        jobSchedule: jobData.schedule,
        jobNumChildren: jobData.numChildren,
        jobChildren: jobData.children || null, // New format with DOB
        jobChildrenAges: jobData.childrenAges || null, // Keep old format for backward compatibility
        parentProfilePictureURL: jobData.profilePictureURL || null,
        parentId: jobData.parentId,
        parentName: jobData.parentName,
        parentEmail: jobData.parentEmail,
        status: 'Pending'
        

      };
  
      await addDoc(collection(db, 'applications'), application);
      alert("You have successfully applied for the job.");
      setIsModalOpen(false);
      setApplicationMessage('');
      window.location.href = '/sitter-jobs';
    } catch (error) {
      console.error("Error applying for job: ", error);
      alert("There was an error applying for the job. Please try again.");
    }
  };

  const fetchFilteredJobs = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(query(collection(db, 'jobs'), where('status', '==', 'Open')));
  
      const allJobs = await Promise.all(
        snapshot.docs.map(async (document) => {
          const job = { id: document.id, ...document.data() };
  
          if (job.location) {
            const { city, state } = await fetchCityAndState(job.location.latitude, job.location.longitude);
            job.city = city;
            job.state = state;
          }
  
          // Get parent profile
          if (job.parentId) {
            const parentDoc = await getDoc(doc(db, 'users', job.parentId));
            if (parentDoc.exists()) {
              const parentData = parentDoc.data();
              job.parentProfilePictureURL = parentData.profilePictureURL || null;
              job.parentName = parentData.name || 'Unknown';
            }
          }
  
          return job;
        })
      );
  
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      let filtered = allJobs.filter((job) => {
        if (!job.endDate) return true;
        const jobEnd = new Date(job.endDate + 'T23:59:59');
        return jobEnd >= todayStart;
      });

      // Only apply filter if toggled
      if (isDistanceFilterEnabled && zipCode.length === 5) {
        const userCoords = getLatLngFromZip(zipCode);
        filtered = filtered.filter((job) => {
          if (!job.zipCode) return false;
          const jobCoords = getLatLngFromZip(job.zipCode);
          if (!userCoords || !jobCoords) return false;
          const distance = haversineDistance(userCoords, jobCoords);
          return distance <= maxDistance;
        });
      }

      const sortedJobs = filtered.sort((a, b) => {
        if (sortOption === 'startDate') return new Date(a.startDate) - new Date(b.startDate);
        if (sortOption === 'payRate') return b.payRange - a.payRange;
        return b.createdAt.seconds - a.createdAt.seconds;
      });

      setJobs(sortedJobs);
    } catch (error) {
      console.error('Error fetching filtered jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
  
        // Get the user's profile address
        const user = auth.currentUser;
        if (!user) {
          alert('User is not authenticated.');
          setLoading(false);
          return;
        }
  
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          alert('User profile not found. Please update your profile.');
          setLoading(false);
          return;
        }
  
        const userAddress = userDoc.data().address;
        if (!userAddress) {
          alert('Address is missing from your profile. Please update your profile.');
          setLoading(false);
          return;
        }

        if (isDistanceFilterEnabled) {
          if (!zipCode || zipCode.trim().length < 5) {
            alert('Please enter a valid ZIP code.');
            setLoading(false);
            return;
          }
        }
  
        if (zipCode) {
          const geoResponse = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              zipCode
            )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDCCNLE719tfQRXV3rqPZBZDBOXR-UGy9E'}`
          );
          const geoData = await geoResponse.json();
          if (geoData.status !== 'OK') {
            alert('Invalid ZIP code. Please enter a valid U.S. ZIP code.');
            setLoading(false);
            return;
          }
        }

        // const jobsWithDistance = allJobs.map((job) => {
        //   const distance = getDistanceInMiles(userLat, userLng, job.latitude, job.longitude);
        //   return { ...job, distance };
        // });
        
        // const filteredJobs = isDistanceFilterEnabled
        //   ? jobsWithDistance.filter((job) => job.distance <= maxDistance)
        //   : jobsWithDistance;
  
        // Fetch jobs
        const snapshot = await getDocs(query(collection(db, 'jobs'), where('status', '==', 'Open')));
        const jobsList = await Promise.all(
          snapshot.docs.map(async (document) => {
            const job = { id: document.id, ...document.data() };
            if (job.location) {
              const { city, state } = await fetchCityAndState(job.location.latitude, job.location.longitude);
              job.city = city;
              job.state = state;
            }

            // Fetch parent's profile picture and name from the users table
            // Only try if user is authenticated (requires auth for user reads)
            if (job.parentId && auth.currentUser) {
              try {
                const parentDoc = await getDoc(doc(db, 'users', job.parentId));
                if (parentDoc.exists()) {
                  const parentData = parentDoc.data();
                  job.parentProfilePictureURL = parentData.profilePictureURL || null;
                  job.parentName = parentData.name || 'Unknown';
                }
              } catch (err) {
                // If permission denied, use job's stored values or defaults
                console.log('Could not fetch parent details, using defaults');
                job.parentProfilePictureURL = job.profilePictureURL || null;
                job.parentName = job.parentName || 'Parent';
              }
            } else if (!auth.currentUser) {
              // Use job's stored values for unauthenticated users
              job.parentProfilePictureURL = job.profilePictureURL || null;
              job.parentName = job.parentName || 'Parent';
            }
            

            return job;
          })
        );

        // Filter out expired jobs (endDate before today)
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const activeJobs = jobsList.filter((job) => {
          if (!job.endDate) return true;
          const jobEnd = new Date(job.endDate + 'T23:59:59');
          return jobEnd >= todayStart;
        });

        // Sort jobs based on the selected option
        const sortedJobs = activeJobs.sort((a, b) => {
          if (sortOption === 'startDate') {
            return new Date(b.startDate) - new Date(a.startDate);
          }
          else if (sortOption === 'payRate') {
            return Number(a.payRange) - Number(b.payRange);
          }
          return b.createdAt.seconds - a.createdAt.seconds; // Sort by date posted by default
        });

        setJobs(sortedJobs);
  
        // // Filter jobs by distance if the filter is enabled
        // if (isDistanceFilterEnabled) {
        //   const userCoords = getLatLngFromZip(zipCode);

        //   const filteredJobs = sortedJobs.filter((job) => {
        //     if (!isDistanceFilterEnabled) return true;

        //     if (!job.zipCode) return false;

        //     const jobCoords = getLatLngFromZip(job.zipCode);
        //     if (!userCoords || !jobCoords) return false;

        //     const distance = haversineDistance(userCoords, jobCoords);
        //     return distance <= maxDistance;
        //   });
        //   setJobs(filteredJobs);
        // } else {
        //   setJobs(sortedJobs);
        // }
        
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (jobs.length > 0) {
      const sortedJobs = [...jobs].sort((a, b) => {
        if (sortOption === 'startDate') {
          return new Date(a.startDate) - new Date(b.startDate);
        } else if (sortOption === 'payRate') {
          return b.payRange - a.payRange;
        }
        return b.createdAt.seconds - a.createdAt.seconds; // Default: date posted
      });
      setJobs(sortedJobs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption, jobs]);
  

  useEffect(() => {
    // Only set up listener if user is authenticated
    if (!auth.currentUser) {
      return;
    }

    // Listen to jobs collection directly for 'Filled' status changes so we can
    // hide filled listings in real-time. Reading Filled jobs doesn't leak PII
    // (jobs are public), and it doesn't require access to applications (which
    // are now restricted to participants).
    const filledJobsQuery = query(
      collection(db, 'jobs'),
      where('status', '==', 'Filled')
    );

    const unsubscribe = onSnapshot(filledJobsQuery, (snapshot) => {
      if (!auth.currentUser) {
        return;
      }

      const acceptedJobs = new Set();
      snapshot.forEach((docSnap) => {
        acceptedJobs.add(docSnap.id);
      });

      setJobs((prevJobs) =>
        prevJobs.filter((job) => !acceptedJobs.has(job.id))
      );
    }, (error) => {
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('Applications listener: permission denied (user may have logged out)');
        return;
      }
      console.error('Error in applications snapshot:', error);
    });

    return () => unsubscribe();
  }, []);


  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="loader"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center sm:text-left mb-8">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-2">
            Job Listings
          </h2>
          <p className="text-slate-600 text-lg">
            Find the perfect babysitting opportunities in your area
          </p>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* ZIP Code Input */}
            <div className="space-y-2">
              <label htmlFor="zipCode" className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                ZIP Code
              </label>
              <input
                type="text"
                id="zipCode"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="e.g. 30301"
                className="w-full p-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200 bg-white/80"
              />
            </div>

            {/* Distance Filter */}
            <div className="space-y-2">
              <label htmlFor="distance" className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Max Distance (Miles)
              </label>
              <input
                type="number"
                id="distance"
                value={maxDistance}
                onChange={(e) => setMaxDistance(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200 bg-white/80"
              />
            </div>

            {/* Sort Option */}
            <div className="space-y-2">
              <label htmlFor="sort" className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Sort By
              </label>
              <select
                id="sort"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200 bg-white/80"
              >
                <option value="datePosted">Date Posted</option>
                <option value="startDate">Start Date</option>
                <option value="payRate">Pay Rate</option>
              </select>
            </div>

            {/* Filter Controls */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="enableDistanceFilter"
                  checked={isDistanceFilterEnabled}
                  onChange={(e) => setIsDistanceFilterEnabled(e.target.checked)}
                  className="w-5 h-5 text-blue-500 bg-white border-slate-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="enableDistanceFilter" className="text-sm font-medium text-slate-700">
                  Filter by Distance
                </label>
              </div>
              
              <button
                onClick={() => fetchFilteredJobs()}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
              >
                <span className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  <span>Apply Filters</span>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Jobs List */}
        {jobs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.815-8.964-2.222m2.964 2.222L21 13.255m0 0a23.931 23.931 0 01-9 3.222m9-3.222H3" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No jobs available</h3>
            <p className="text-slate-500">
              No job postings are currently available in your area.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="group bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden"
              >
                {/* Job Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        Open
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      Posted {new Date(job.createdAt.seconds * 1000).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Parent Profile Picture */}
                    {job.parentProfilePictureURL && job.parentId && (
                      <a href={`/profile/${job.parentId}`} className="self-center lg:self-start">
                        <div className="relative group">
                          <img
                            src={job.parentProfilePictureURL}
                            alt="Parent Profile"
                            className="w-24 h-24 lg:w-32 lg:h-32 rounded-2xl object-cover shadow-lg border-4 border-white group-hover:border-blue-200 transition-colors duration-200"
                          />
                        </div>
                      </a>
                    )}

                    {/* Job Details */}
                    <div className="flex-1 space-y-4">
                      {/* Job Title and Pay Rate */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="text-2xl font-bold text-slate-800 mb-2">{job.title}</h3>
                          <p className="text-slate-600 leading-relaxed">{job.description}</p>
                        </div>
                        <div className="mt-3 sm:mt-0">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold text-lg">
                            ${job.payRange}/hr
                          </div>
                        </div>
                      </div>

                      {/* Job Details Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Start Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(job.startDate)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Start Time</p>
                              <p className="font-semibold text-slate-800">{formatTimeToAMPM(job.jobStartTime)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">End Date</p>
                              <p className="font-semibold text-slate-800">{formatLocalDate(job.endDate)}</p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
                              <p className="font-semibold text-slate-800">{job.city}, {job.state}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Children Ages */}
                      {((job.children && job.children.length > 0) || (job.childrenAges && job.childrenAges.length > 0)) && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Children's Ages</h4>
                          <div className="flex flex-wrap gap-2">
                            {job.children ? (
                              job.children.map((child, index) => (
                                <span key={child.id || index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {child.name && `${child.name}: `}{formatAge(child.age)} old
                                </span>
                              ))
                            ) : (
                              job.childrenAges.map((age, index) => (
                                <span key={index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {age.value} {age.unit} old
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="pt-4 border-t border-slate-200/50">
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={async () => {
                              const jobDoc = await getDoc(doc(db, 'jobs', job.id));
                              const jobData = jobDoc.data();
                              setSelectedJobForTimes(jobData);
                              setIsTimesModalOpen(true);
                            }}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transition-colors duration-200 shadow-lg shadow-purple-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>View Schedule</span>
                            </span>
                          </button>

                          {userType === 'sitter' && (
                            appliedJobs.has(job.id) ? (
                              <span className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-xl text-center">
                                <span className="flex items-center justify-center space-x-2">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Applied</span>
                                </span>
                              </span>
                            ) : (
                              <button
                                onClick={() => applyForJob(job.id)}
                                className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-colors duration-200 shadow-lg shadow-blue-500/25"
                              >
                                <span className="flex items-center justify-center space-x-2">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  <span>Apply Now</span>
                                </span>
                              </button>
                            )
                          )}
                          {job.parentId && (
                            <a
                              href={`/profile/${job.parentId}`}
                              className="flex-1 sm:flex-none inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transition-colors duration-200 shadow-lg shadow-slate-500/25"
                            >
                              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              View Parent Profile
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(message) => {
          setApplicationMessage(message);
          handleModalSubmit(message);
        }}
        initialMessage={applicationMessage}
      />
  
      <JobTimesModal
        isOpen={isTimesModalOpen}
        onClose={() => setIsTimesModalOpen(false)}
        job={selectedJobForTimes}
      />
    </div>
  );
};

export default JobList;